"use client"

import styled from "@emotion/styled"
import { Box, IconButton } from "@mui/material"
import { useRef, useState } from "react"
import {
  isVideo,
  PauseIcon,
  Play2Icon,
  ResponsiveMedia,
  useIsDesktop,
} from "ui"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledMedia = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
    justify-content: center;
    /* Design ratio 1390:1032 (Figma 1920/1440 frames). Width follows the
       viewport (934px @1440 → 1390px @1920) but is also capped by the
       available height so the image is never cropped (EGDW-434). */
    aspect-ratio: 1390 / 1032;
    width: min(calc(95vw - 434px), 1390px, calc((100vh - 48px) * 1390 / 1032));
    height: auto;

    flex: none;
  }
  img,
  video {
    object-fit: cover;
    width: 100%;
    height: 100%;
    ${(props) => props.theme.breakpoints.up("md")} {
      border-radius: 20px;
    }
  }
`

const ControlButton = styled(IconButton)<{ $isPlaying: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease-out;
  color: white;
  & svg {
    color: inherit;
  }
  &:hover {
    background-color: #0e2846;
    color: white;
  }
`

type TMedia = {
  id?: number
  url: string
  altText: string | null
}

type Props = {
  imageForDesktop: TMedia
  imageForMobile: TMedia
}

const LandingMedia = ({ imageForDesktop, imageForMobile }: Props) => {
  const isDesktop = useIsDesktop()
  const desktopUrl = getUmbracoMediaUrl(imageForDesktop.url)
  const mobileUrl = getUmbracoMediaUrl(imageForMobile.url)
  // video playback state
  const [isPlaying, setIsPlaying] = useState(true)
  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)

  const hasVideoMedia = isDesktop ? isVideo(desktopUrl) : isVideo(mobileUrl)

  const syncPlaybackState = (shouldPlay: boolean) => {
    // eslint-disable-next-line no-extra-semi
    ;[desktopVideoRef.current, mobileVideoRef.current].forEach((video) => {
      if (!video) return
      if (shouldPlay) {
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => undefined)
        }
      } else {
        video.pause()
      }
    })
  }

  const handlePlaybackToggle = () => {
    setIsPlaying((prev) => {
      const next = !prev
      syncPlaybackState(next)
      return next
    })
  }

  return (
    <StyledMedia>
      <ResponsiveMedia
        desktopMedia={desktopUrl}
        mobileMedia={mobileUrl}
        alt={
          imageForDesktop.altText || imageForMobile.altText || "Landing media"
        }
        mobileAlt={imageForMobile.altText}
        desktopVideoRef={desktopVideoRef}
        mobileVideoRef={mobileVideoRef}
        priority
        quality={100}
        isMobileStatic
      />
      {hasVideoMedia && (
        <Box
          component={"span"}
          sx={{
            position: { xs: "absolute" },
            top: {
              xs: 24,
              md: "unset",
            },
            bottom: {
              md: 60,
            },
            insetInlineEnd: {
              xs: 20,
              md: 120,
            },
            zIndex: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ControlButton
            aria-label={
              isPlaying ? "Pause background video" : "Play background video"
            }
            onClick={handlePlaybackToggle}
            disableRipple
            $isPlaying={isPlaying}
          >
            {isPlaying ? <PauseIcon /> : <Play2Icon />}
          </ControlButton>
        </Box>
      )}
    </StyledMedia>
  )
}

export default LandingMedia
